/**
 * Lumo Techniques Library Page
 * Browse and learn proprietary Lumo objection handling techniques
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import lumoTechniquesService from '../../services/lumoTechniquesService';
import { coachingConceptsService } from '../../services/coachingConceptsService';
import { drillsService } from '../../services/drillsService';
import NavigationBar from '../../components/ui/NavigationBar';
import LoadingState from '../../components/ui/LoadingState';
import TechniqueCard from '../../components/TechniqueCard';
import Icon from '../../components/AppIcon';

const CATEGORY_FILTERS = [
  { value: 'all', label: 'All Techniques' },
  { value: 'acknowledge_pivot', label: 'Acknowledge & Pivot' },
  { value: 'detached_close', label: 'Detached Close' },
  { value: 'speed_drill', label: 'Speed Drill' },
  { value: 'cascade_challenge', label: 'Cascade Challenge' },
  { value: 'reset_move', label: 'Reset Move' },
  { value: 'frame_flip', label: 'Frame Flip' },
  { value: 'exit_ramp', label: 'Exit Ramp' },
  { value: 'concept', label: 'Concepts' }
];

const DIFFICULTY_FILTERS = [
  { value: 'all', label: 'All Levels' },
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' }
];

const TechniquesLibrary = () => {
  const navigate = useNavigate();
  const { user, profile } = useAuth();

  const [techniques, setTechniques] = useState([]);
  const [userMastery, setUserMastery] = useState([]);
  const [conceptTechniques, setConceptTechniques] = useState([]);
  const [skillLevel, setSkillLevel] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [categoryFilter, setCategoryFilter] = useState('all');
  const [difficultyFilter, setDifficultyFilter] = useState('all');
  const [selectedTechnique, setSelectedTechnique] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadTechniquesAndConcepts();
  }, [user, navigate]);

  const calculateSkillLevel = async () => {
    try {
      if (!profile?.id) return 1;
      const userDrills = await drillsService?.getUserDrillHistory(100);
      if (!userDrills || userDrills.length === 0) return 1;
      const avgScore = userDrills.reduce((sum, drill) => sum + (drill?.score || 0), 0) / userDrills.length;
      if (avgScore < 40) return 1;
      if (avgScore < 60) return 2;
      if (avgScore < 75) return 3;
      if (avgScore < 90) return 4;
      return 5;
    } catch (err) {
      console.error('Error calculating skill level:', err);
      return 1;
    }
  };

  const slugify = (value = '') =>
    value
      ?.toString()
      ?.toLowerCase()
      ?.trim()
      ?.replace(/[^a-z0-9]+/g, '-')
      ?.replace(/(^-|-$)+/g, '') || 'concept';

  const mapConceptDifficultyToLevel = (difficulty) => {
    const level = Number(difficulty) || 1;
    if (level <= 1) return 'beginner';
    if (level <= 3) return 'intermediate';
    return 'advanced';
  };

  const transformConceptToTechnique = (concept) => ({
    id: `concept-${concept?.id}`,
    conceptId: concept?.id,
    name: concept?.title,
    slug: `concept-${slugify(concept?.title || concept?.id)}`,
    tagline: concept?.summary || 'Foundational concept to reinforce technique fundamentals.',
    category: 'concept',
    step1: concept?.bulletPoints?.[0],
    step2: concept?.bulletPoints?.[1],
    step3: concept?.bulletPoints?.[2],
    exampleExecution: concept?.exampleScript,
    difficultyLevel: mapConceptDifficultyToLevel(concept?.difficulty),
    bestForObjections: Array.isArray(concept?.tags)
      ? concept.tags
      : (typeof concept?.tags === 'string'
          ? concept.tags.split(',').map(t => t.trim()).filter(Boolean)
          : []),
    isConcept: true
  });

  const loadTechniquesAndConcepts = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const level = await calculateSkillLevel();
      setSkillLevel(level);

      // Load all techniques
      const techniquesData = await lumoTechniquesService.getAllTechniques();
      const normalizedTechniques = (techniquesData || []).map(t => ({ ...t, isConcept: false }));
      setTechniques(normalizedTechniques);

      // Load user mastery for authenticated users
      if (user) {
        const masteryData = await lumoTechniquesService.getUserTechniqueMastery(user.id);
        setUserMastery(masteryData);
      }

      // Load coaching concepts and filter by skill level
      const allConcepts = await coachingConceptsService?.getAll();
      const filteredConcepts = allConcepts?.filter(concept => concept?.difficulty <= level) || [];
      const conceptAsTechniques = filteredConcepts.map(transformConceptToTechnique);
      setConceptTechniques(conceptAsTechniques);
    } catch (err) {
      console.error('Error loading techniques:', err);
      setError('Failed to load techniques/concepts. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getMasteryForTechnique = (techniqueName) => {
    return userMastery.find(m => m.techniqueName === techniqueName);
  };

  const combinedTechniques = [...techniques, ...conceptTechniques];

  const filteredTechniques = combinedTechniques.filter(technique => {
    const categoryMatch = categoryFilter === 'all' || technique.category === categoryFilter;
    const difficultyMatch = difficultyFilter === 'all' || technique.difficultyLevel === difficultyFilter;
    return categoryMatch && difficultyMatch;
  });

  const handlePracticeTechnique = (technique) => {
    if (technique?.isConcept) {
      const cid = technique?.conceptId || technique?.id?.toString()?.replace('concept-','');
      navigate(`/app/concepts-practice/${cid}`);
    } else {
      navigate(`/app/technique-practice/${technique.slug}`);
    }
  };

  const skillLabel = {
    1: 'Beginner',
    2: 'Intermediate',
    3: 'Advanced',
    4: 'Expert',
    5: 'Master'
  }[skillLevel] || 'Beginner';

  if (isLoading) {
    return <LoadingState message="Loading Lumo techniques..." />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <NavigationBar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-primary-100 rounded-xl">
              <Icon name="GraduationCap" className="w-8 h-8 text-primary-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Techniques & Concepts</h1>
              <p className="text-gray-600">Proprietary moves plus core concepts, tailored to your level ({skillLabel})</p>
            </div>
          </div>
          <p className="text-gray-700 mt-2">
            Master the branded objection-handling techniques and foundational concepts that power Lumo's coaching. These aren't generic scripts — they're fight moves for sales conversations.
          </p>
        </div>

        {/* Error state */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-lg flex items-start gap-3">
            <Icon name="AlertCircle" className="w-6 h-6 text-red-600 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-red-900">Error Loading Techniques</h3>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Stats Overview */}
        {userMastery.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white p-4 rounded-xl border-2 border-gray-200">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="Flame" className="w-5 h-5 text-orange-500" />
                <p className="text-sm text-gray-600">Techniques Practiced</p>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {userMastery.filter(m => m.timesAttempted > 0).length}
              </p>
            </div>

            <div className="bg-white p-4 rounded-xl border-2 border-gray-200">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="Star" className="w-5 h-5 text-yellow-500" />
                <p className="text-sm text-gray-600">Mastered</p>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {userMastery.filter(m => m.masteryLevel === 'master').length}
              </p>
            </div>

            <div className="bg-white p-4 rounded-xl border-2 border-gray-200">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="BarChart" className="w-5 h-5 text-blue-500" />
                <p className="text-sm text-gray-600">Total Attempts</p>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {userMastery.reduce((sum, m) => sum + (m.timesAttempted || 0), 0)}
              </p>
            </div>

            <div className="bg-white p-4 rounded-xl border-2 border-gray-200">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="Trophy" className="w-5 h-5 text-green-500" />
                <p className="text-sm text-gray-600">Avg Success</p>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {Math.round(
                  userMastery.filter(m => m.timesAttempted > 0)
                    .reduce((sum, m) => sum + (m.successRate || 0), 0) / 
                  Math.max(userMastery.filter(m => m.timesAttempted > 0).length, 1) * 100
                )}%
              </p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="mb-6 bg-white p-4 rounded-xl border-2 border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Category filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Category
              </label>
              <div className="flex flex-wrap gap-2">
                {CATEGORY_FILTERS.map(filter => (
                  <button
                    key={filter.value}
                    onClick={() => setCategoryFilter(filter.value)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      categoryFilter === filter.value
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>
            {/* Difficulty filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Difficulty
              </label>
              <div className="flex flex-wrap gap-2">
                {DIFFICULTY_FILTERS.map(filter => (
                  <button
                    key={filter.value}
                    onClick={() => setDifficultyFilter(filter.value)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      difficultyFilter === filter.value
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Techniques Grid */}
        {filteredTechniques.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Search" className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Techniques Found</h3>
            <p className="text-gray-600">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredTechniques.map(technique => (
              <TechniqueCard
                key={technique.id}
                technique={technique}
                userMastery={getMasteryForTechnique(technique.name)}
                onPractice={handlePracticeTechnique}
                showFullDetails={true}
              />
            ))}
          </div>
        )}

        {/* Help section */}
        <div className="mt-12 p-6 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-xl border-2 border-primary-200">
          <div className="flex items-start gap-4">
            <Icon name="Lightbulb" className="w-8 h-8 text-primary-600 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">How to Master Techniques</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <Icon name="CheckCircle" className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span><strong>Novice:</strong> Just starting — focus on understanding the steps</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="CheckCircle" className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span><strong>Learning:</strong> 3+ attempts, 60%+ execution score — getting the hang of it</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="CheckCircle" className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span><strong>Proficient:</strong> 5+ attempts, 70%+ execution score — solid fundamentals</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="CheckCircle" className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span><strong>Master:</strong> 10+ attempts, 80%+ execution score — technique is second nature</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div></div>
  );
};

export default TechniquesLibrary;
